/* eslint-disable @typescript-eslint/no-explicit-any -- casts formulaire / handlers comme U3104 */
/* eslint-disable react-hooks/exhaustive-deps -- effets chargement id */
import React, { useEffect, useReducer, useState } from "react";
import type { U2000FormSBusinessHandlers, U2000FormStateHandlersReturn } from "../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import useU2000AxiosAuthenticated from "../../u2000Core/axios/U2000UseAxiosAuthenticated";

import U2000DefaultForm from "../../u2000Core/components/formControls/U2000DefaultForm";

import { useToast } from "../../u2000Core/context/toast/U2000ToastContext";
import { U2000HandleApiError } from "../../u2000Core/axios/U2000HandleApiError";
import { useU2000GetHandlers } from "../../u2000Core/hooks/handler/U2000UseGetHandlers";
import { u2000IntFormState, useU2000FormStateHandlers } from "../../u2000Core/reducer/formReducer/U2000FormStateHandlers";
import { useU2000SearchHandlers } from "../../u2000Core/hooks/handler/U2000UseSearchHandlers";
import type { U2000SearchPayload, U2000SearchResult } from "../../u2000Core/components/tables/basic/U2000BasicTable";
import { U2000GridStateEnumPaginationDirection } from "../../u2000Core/reducer/formReducer/U2000GridState";
import { useU2000SaveHandlers } from "../../u2000Core/hooks/handler/U2000UseSaveHandlers";
import u2000FormState, { U2000FormStateEnumEditMode, type U2000FormState } from "../../u2000Core/reducer/formReducer/U2000FormState";
import { U2000LUStateEnumNextAction } from "../../u2000Core/reducer/formReducer/U2000LUState";
import { useU2000DeleteHandlers } from "../../u2000Core/hooks/handler/U2000UseDeleteHandlers";
import { U2000GridStateDeleteAction, type U2000GridStateNextRecordAfterDeleteReturn } from "../../u2000Core/reducer/formReducer/U2000GridStateHandlers";
import { NS, U3105ROLES } from "../U3105";
import U3105PayrollYearBody from "./form/u3105PayrollYear/U3105PayrollYearBody";
import { U3105PayrollYearFormToolBar } from "./form/u3105PayrollYear/U3105PayrollYearFormToolBar";
import U3105PayrollYearDeleteDlg from "./common/U3105PayrollYearDeleteDlg";
import U3105NewPayrollYearDialog from "./common/U3105NewPayrollYearDialog";
import { useU3105Context } from "../U3105Context";
import U2000AuditPanelComponent from "../../u2000Core/components/audit/U2000AuditPanelComponent";
import {
  buildCreateBody,
  buildDeleteBody,
  buildUpdateBody,
  doCreateData,
  doDeleteData,
  doGetAuditsData,
  doGetData,
  doSearch,
  doUpdateData,
  buildPayrollPeriodsFromFirstMonday,
  createEmptyU3105FormRow,
  formToSearchRow,
  luHandleAppiError,
  normalizeU3105FromApi,
  U3105_MAX_PAYROLL_PERIODS,
  U3105_MIN_PAYROLL_PERIODS,
  type U3105PayrollYearFormRow,
  type U3105PayrollYearGet,
  type U3105PayrollYearSearchRow,
  type U3105PayrollYearWithOrg,
} from "../U3105Service";
import { useU2000AuthContext } from "../../u2000Core/context/auth/U2000UseAuthContext";
import { useU2000LogicalUnitContext } from "../../u2000Core/context/logicalUnit/U2000LogicalUnitContext";
import U2000Tab from "../../u2000Core/components/tab/U2000Tab";

function normalizeForForm(row: U3105PayrollYearGet | U3105PayrollYearWithOrg): U3105PayrollYearFormRow {
  return normalizeU3105FromApi(row);
}

function parseDateMs(s: string): number | null {
  const t = String(s ?? "").trim();
  if (!t) return null;
  const d = new Date(t);
  return Number.isNaN(d.getTime()) ? null : d.getTime();
}

function payrollPeriodNumbersAreContiguous(periods: { periodNumber: number }[]): boolean {
  const n = periods.length;
  if (n === 0) return false;
  const nums = periods.map((p) => p.periodNumber).sort((a, b) => a - b);
  if (nums.length !== n) return false;
  for (let i = 0; i < n; i += 1) {
    if (nums[i] !== i + 1) return false;
  }
  return true;
}

export interface U3105PayrollYearFormExtendedHandlers extends U2000FormStateHandlersReturn<U3105PayrollYearFormRow> {
  addNewPayrollYear: () => void;
  modifyPayrollYear: () => void;
  deletePayrollYear: () => Promise<void>;
}

const U3105PayrollYearForm: React.FC = () => {
  const { id } = useParams();
  const { hasRole } = useU2000AuthContext();
  const { luStateHandlers, searchStateHandlers, gridStateHandlers } = useU3105Context();
  const { setLogicalUnitBreadcrumb } = useU2000LogicalUnitContext();

  const [activeTab, setActiveTab] = useState<string | null>(null);
  const [newPayrollWizardComplete, setNewPayrollWizardComplete] = useState(false);
  const [newPayrollWizardFromFormOpen, setNewPayrollWizardFromFormOpen] = useState(false);
  const [formState, formStateDispatch] = useReducer<U2000FormState, any>(u2000FormState, u2000IntFormState);

  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isFr = (i18n.resolvedLanguage ?? i18n.language).startsWith("fr");
  const listTitle = t("title", { ns: NS });
  const toast = useToast();
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();

  const validateForm = (row: U3105PayrollYearFormRow) => {
    const result: Record<string, string> = {};
    const year = row.yearCode?.trim() ?? "";
    if (!year) {
      result["yearCode"] = t("errYearCodeRequired", {
        ns: NS,
        defaultValue: isFr ? "Le code année est obligatoire." : "Year code is required.",
      });
    } else if (year.length > 50) {
      result["yearCode"] = t("errYearCodeLength", {
        ns: NS,
        defaultValue: isFr ? "Le code année est trop long." : "Year code is too long.",
      });
    }

    const periods = row.payrollPeriods ?? [];
    if (periods.length < U3105_MIN_PAYROLL_PERIODS || periods.length > U3105_MAX_PAYROLL_PERIODS) {
      result["payrollPeriods"] = t("errPeriodsCount", {
        ns: NS,
        defaultValue: isFr ? "Entre 1 et 27 périodes sont requises." : "Between 1 and 27 periods are required.",
      });
    }

    const nums = periods.map((p) => p.periodNumber);
    const uniq = new Set(nums);
    if (
      uniq.size !== nums.length ||
      nums.some((n) => n < 1 || n > U3105_MAX_PAYROLL_PERIODS || !Number.isInteger(n)) ||
      !payrollPeriodNumbersAreContiguous(periods)
    ) {
      result["payrollPeriods"] = t("errPeriodNumbersInvalid", {
        ns: NS,
        defaultValue: isFr
          ? "Les numéros de période doivent être 1 à N (N = nombre de périodes), tous distincts et contigus."
          : "Period numbers must be 1 through N (N = count), distinct and contiguous.",
      });
    }

    periods.forEach((p, i) => {
      const base = `payrollPeriods.${i}`;
      const startMs = parseDateMs(p.startAt);
      const endMs = parseDateMs(p.endAt);
      if (startMs === null) {
        result[`${base}.startAt`] = t("errPeriodDateInvalid", { ns: NS });
      }
      if (endMs === null) {
        result[`${base}.endAt`] = t("errPeriodDateInvalid", { ns: NS });
      }
      if (startMs !== null && endMs !== null && startMs >= endMs) {
        result[`${base}.endAt`] = t("errPeriodEndAfterStart", { ns: NS });
      }
    });

    return result;
  };

  const u3105BL: U2000FormSBusinessHandlers<U3105PayrollYearFormRow> = {
    newRecord: () => createEmptyU3105FormRow(),

    validateForm,

    saveForme: async (row: U3105PayrollYearFormRow, editMode: U2000FormStateEnumEditMode) => {
      const errors = validateForm(row);
      if (Object.keys(errors).length > 0) {
        u3105FormExtended.setErrors(errors);
        return;
      }
      handleSave.saveData(row, editMode);
    },

    cancelForm: () => {
      const old = u3105FormExtended.getOldData() as U3105PayrollYearFormRow | null;
      if (old) {
        u3105FormExtended.setData({ ...old });
      }
      if (luStateHandlers.getActionOriginFrom() === "/U3105") {
        navigate(luStateHandlers.getActionOriginFrom(), { replace: true });
      }
    },

    onChangeRecordForm: (row) => {
      if (row.id) {
        handleGet.getData(row.id);
      }
    },

    onChangePageForm: (page: number, direction: U2000GridStateEnumPaginationDirection) => {
      handleSearch.handleSearch(searchStateHandlers.extractSearchParameter(), gridStateHandlers.extractPagination(page), direction);
    },

    onLeaveForm: () => {
      navigate("/U3105");
    },
  };

  const u3105FormHandlers = useU2000FormStateHandlers<U3105PayrollYearFormRow>({
    formState,
    formStateDispatch,
    businessHandlers: u3105BL,
  });

  const u3105FormExtended: U3105PayrollYearFormExtendedHandlers = {
    ...u3105FormHandlers,
    addNewPayrollYear: () => {
      setNewPayrollWizardFromFormOpen(true);
    },
    modifyPayrollYear: () => {
      u3105FormExtended.setEditMode(U2000FormStateEnumEditMode.EDIT);
    },
    deletePayrollYear: async () => {
      handleDelete.handleDelete();
    },
  };

  const handleSearch = useU2000SearchHandlers<U3105PayrollYearSearchRow>({
    doSearchFn: (searchPayload: U2000SearchPayload) => {
      return doSearch(u2000AxiosAuthenticated, searchPayload);
    },
    onSuccess: (searchResult: U2000SearchResult<U3105PayrollYearSearchRow>, direction: U2000GridStateEnumPaginationDirection) => {
      const idx = gridStateHandlers.initGridAfterSearch(searchResult, direction);
      handleGet.getData(searchResult.data[idx].id);
    },
    onError: (error: unknown) => {
      const errorResult = U2000HandleApiError(error as any, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  const handleGet = useU2000GetHandlers<U3105PayrollYearFormRow>({
    doGetFn: (rowId: string) => {
      return doGetData(u2000AxiosAuthenticated, rowId);
    },
    onSuccess: (row: U3105PayrollYearFormRow) => {
      const normalized = normalizeForForm(row as unknown as U3105PayrollYearGet);
      gridStateHandlers.setDataById(normalized.id, formToSearchRow(normalized));
      u3105FormExtended.setData(normalized);

      const newPath = location.pathname.replace(/(?:new|[\w-]+)$/, String(row.id));
      navigate(newPath, { replace: true });

      const crumbLabel = `${normalized.yearCode}`;
      setLogicalUnitBreadcrumb([
        { href: "/U3105", label: listTitle },
        { label: crumbLabel },
      ]);

      if (luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.MODIFY) {
        u3105FormExtended.setEditMode(U2000FormStateEnumEditMode.EDIT);
      }
    },
    onError: (error: unknown) => {
      const errorResult = U2000HandleApiError(error as any, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  const handleSave = useU2000SaveHandlers<U3105PayrollYearFormRow>({
    doSaveFn: (row: U3105PayrollYearFormRow, editMode: U2000FormStateEnumEditMode) => {
      if (editMode === U2000FormStateEnumEditMode.ADD) {
        return doCreateData(u2000AxiosAuthenticated, buildCreateBody(row));
      }
      if (editMode === U2000FormStateEnumEditMode.EDIT) {
        return doUpdateData(u2000AxiosAuthenticated, row.id, buildUpdateBody(row));
      }
      return Promise.resolve(undefined);
    },
    onSuccess: (row: U3105PayrollYearFormRow, editMode: U2000FormStateEnumEditMode) => {
      const normalized = normalizeForForm(row as unknown as U3105PayrollYearWithOrg);
      if (editMode === U2000FormStateEnumEditMode.ADD) {
        gridStateHandlers.setData([...gridStateHandlers.getData(), formToSearchRow(normalized)]);
        gridStateHandlers.setTotalRows(gridStateHandlers.getTotalRows() + 1);
        gridStateHandlers.setCurrentIdx(gridStateHandlers.getData().length);
        const newPath = location.pathname.replace(/(?:new|[\w-]+)$/, String(row.id));
        navigate(newPath, { replace: true });
      } else if (editMode === U2000FormStateEnumEditMode.EDIT) {
        gridStateHandlers.setDataById(row.id, formToSearchRow(normalized));
      }
      u3105FormExtended.afterSaveData(normalized);
      toast.success(t("msgLogicalUnitSaved", { ns: NS }));

      if (luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.ADD || luStateHandlers.getNextAction() === U2000LUStateEnumNextAction.MODIFY) {
        if (luStateHandlers.getActionOriginFrom() === "/U3105") {
          navigate(luStateHandlers.getActionOriginFrom(), { replace: true });
        }
      }
    },
    onError: (error: unknown) => {
      const errorResult = U2000HandleApiError(error as any, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      } else if (errorResult.otherErrors && Object.keys(errorResult.otherErrors).length > 0) {
        u3105FormExtended.setOtherErrors(errorResult.otherErrors);
      } else if (errorResult.fieldErrors && Object.keys(errorResult.fieldErrors).length > 0) {
        u3105FormExtended.setErrors(errorResult.fieldErrors);
      }
    },
  });

  const handleDelete = useU2000DeleteHandlers({
    getSelectedRows: () => [u3105FormExtended.getData()],
    doDeleteFn: async (rowId, row) => {
      const r = row as U3105PayrollYearFormRow;
      return await doDeleteData(u2000AxiosAuthenticated, rowId, buildDeleteBody(r));
    },

    onSuccess: (deletedRows) => {
      let newData = gridStateHandlers.getData();
      const currentIdx = gridStateHandlers.getCurrentIdx();
      let totalRows = gridStateHandlers.getTotalRows();

      deletedRows.forEach((del) => {
        toast.success(t("success0003", { key: (del as U3105PayrollYearFormRow).yearCode, ns: NS }), t("successTitle", { ns: NS }));
        newData = newData.filter((dataRow) => dataRow.id !== del?.id);
        totalRows -= 1;
      });
      gridStateHandlers.setData(newData);
      gridStateHandlers.setTotalRows(totalRows);

      const action: U2000GridStateNextRecordAfterDeleteReturn = gridStateHandlers.getNextRecordAfterDelete(currentIdx, newData, totalRows);
      if (action.action === U2000GridStateDeleteAction.RECORD && action.id) {
        handleGet.getData(action.id);
      } else if (action.action === U2000GridStateDeleteAction.PAGENEXT && action.page) {
        handleSearch.handleSearch(
          searchStateHandlers.extractSearchParameter(),
          gridStateHandlers.extractPagination(action.page),
          U2000GridStateEnumPaginationDirection.PREVIOUSPAGE,
        );
      } else if (action.action === U2000GridStateDeleteAction.PAGEPREVIOUS && action.page) {
        handleSearch.handleSearch(
          searchStateHandlers.extractSearchParameter(),
          gridStateHandlers.extractPagination(action.page),
          U2000GridStateEnumPaginationDirection.NEXTPAGE,
        );
      } else if (action.action === U2000GridStateDeleteAction.LIST) {
        navigate("/U3105", { replace: true });
      }
    },

    onError: (errors) => {
      errors.forEach(({ error }) => {
        const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError);
        if (errorResult.genericErrors?.length) {
          errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
        }
      });
    },
  });

  useEffect(() => {
    if (id === undefined) return;
    setNewPayrollWizardFromFormOpen(false);
    if (id === "new") {
      setNewPayrollWizardComplete(false);
    } else {
      setNewPayrollWizardComplete(true);
      handleGet.getData(id);
    }
  }, [id]);

  const applyNewPayrollWizard = (yearCode: string, firstMondayYmd: string) => {
    const payrollPeriods = buildPayrollPeriodsFromFirstMonday(firstMondayYmd, yearCode);
    if (payrollPeriods.length === 0) return;
    u3105FormExtended.setEditMode(U2000FormStateEnumEditMode.ADD);
    u3105FormExtended.setData({
      id: "",
      yearCode,
      version: 0,
      payrollPeriods,
    });
    setNewPayrollWizardComplete(true);
    setNewPayrollWizardFromFormOpen(false);
    setLogicalUnitBreadcrumb([
      { href: "/U3105", label: listTitle },
      { label: t("breadcrumbNew", { ns: NS }) },
    ]);
  };

  const handleNewPayrollWizardCancel = () => {
    setNewPayrollWizardFromFormOpen(false);
    if (id === "new") {
      navigate("/U3105", { replace: true });
    }
  };

  if (id === "new" && !newPayrollWizardComplete) {
    return (
      <U3105NewPayrollYearDialog
        isOpen={true}
        onApply={applyNewPayrollWizard}
        onCancel={handleNewPayrollWizardCancel}
        t={t}
        NS={NS}
      />
    );
  }

  if (u3105FormExtended.getData() === null) {
    return null;
  }

  const formTabs = [
    hasRole(U3105ROLES.AUDITS) && {
      id: "tabAudits",
      label: t("tabAudits", { ns: NS }),
      content: (
        <U2000AuditPanelComponent
          id={u3105FormExtended.getData()?.id}
          doGetAuditsData={doGetAuditsData}
          toastName="U3105PayrollYearAudit"
        />
      ),
    },
  ].filter(Boolean) as { id: string; label: string; content: React.ReactNode }[];

  return (
    <>
      {newPayrollWizardFromFormOpen && (
        <U3105NewPayrollYearDialog
          isOpen={true}
          onApply={applyNewPayrollWizard}
          onCancel={handleNewPayrollWizardCancel}
          t={t}
          NS={NS}
        />
      )}

      <U3105PayrollYearDeleteDlg handleDelete={handleDelete as any} t={t} NS={NS} />

      <U2000DefaultForm
        actionsBtns={<U3105PayrollYearFormToolBar formStateHandlers={u3105FormExtended as any} />}
        searchStateHandlers={searchStateHandlers}
        gridStateHandlers={gridStateHandlers}
        formStateHandlers={u3105FormExtended as any}
      >
        <U3105PayrollYearBody formStateHandlers={u3105FormExtended as any} t={t} />

        {formTabs.length > 0 && (
          <U2000Tab
            className="mt-4"
            tabs={formTabs}
            defaultTab={formTabs[0]?.id}
            activeTab={activeTab ?? formTabs[0]?.id}
            setActiveTab={(tab) => setActiveTab(typeof tab === "string" ? tab : String(tab))}
          />
        )}
      </U2000DefaultForm>
    </>
  );
};

export default U3105PayrollYearForm;
